import { z } from "zod";
import { doctorNoticeBodySchema } from "./doctor-notice";
import { documentMeetingSchema } from "./meeting";
import { approveOnboardingBodySchema } from "./prescription-cycle";
import { createSubscriptionLinkSchema } from "./subscription-plan";
import { confirmPrescriptionChoiceSchema } from "./treatment-start";

// TES-1185 (TES-1180 points 1 and 4) — when the meeting needs no
// kompletteringar, its own final step publishes the treatment decision.
// A-02 2026-10-08: "Om inga kompletteringar krävs är behandlingen slutligt
// fastställd. … Kirill för in journalanteckningen i Webdoc, skapar
// e-receptet manuellt i Webdoc och bekräftar separat Journalanteckningen är
// införd i Webdoc och Receptet är utfärdat i Webdoc." One guided step instead
// of documenting the meeting and then opening the decision.
//
// POST …/meetings/:meetingId/document-and-decide: the meeting's own body
// (documentMeetingSchema, outcome "Underlaget är tillräckligt – gå vidare",
// no notice of its own) and the positive decision. One request, one
// transaction: the meeting is documented, its note with the decision is
// stored as one final version (`noteText`, the text the doctor copied to
// Webdoc, must be exactly what the server compiles) and the decision with
// its prescription status is published.
export const meetingDecisionSchema = z.object({
  approval: approveOnboardingBodySchema,
  subscription: createSubscriptionLinkSchema,
  // "Receptet är utfärdat i Webdoc" / "Befintligt recept gäller fortfarande".
  prescription: confirmPrescriptionChoiceSchema,
  // "Läkarens besked till patienten · visas under Din vård", published with
  // the decision (the meeting publishes none of its own here).
  patientSummary: doctorNoticeBodySchema.optional(),
  // The meeting-and-decision note ("vårdmötes- och beslutsanteckning") as
  // the step showed it.
  noteText: z.string().min(1).max(60000),
});
export type MeetingDecision = z.infer<typeof meetingDecisionSchema>;

export const documentMeetingAndDecideSchema = z
  .object({
    meeting: documentMeetingSchema,
    decision: meetingDecisionSchema,
  })
  .superRefine((v, ctx) => {
    if (v.meeting.completion?.outcome !== "proceed") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["meeting", "completion", "outcome"],
        message: "decision_only_when_proceeding",
      });
    }
    if (v.meeting.patientNotice) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["meeting", "patientNotice"],
        message: "notice_goes_with_the_decision",
      });
    }
  });
export type DocumentMeetingAndDecide = z.infer<
  typeof documentMeetingAndDecideSchema
>;
// What the admin sends (defaults not yet applied).
export type DocumentMeetingAndDecideRequest = z.input<
  typeof documentMeetingAndDecideSchema
>;
